import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Plus, Undo2, Redo2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { TransactionFilters, TransactionTable, TransactionForm } from "./TransactionComponents";
import { TransactionInvoiceDialog } from "./TransactionInvoiceDialog";
import { useAuth } from "../context/AuthContext";

export default function Transactions() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isAssistant = user?.role === "assistant";
  const isStaff = isAdmin || isAssistant;

  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [tags, setTags] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const [txDialogOpen, setTxDialogOpen] = useState(false);
  const [editTxId, setEditTxId] = useState(null);
  const [type, setType] = useState("income");
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("TL");
  const [accountId, setAccountId] = useState("manual");
  const [accountName, setAccountName] = useState("");
  const [paymentMethodId, setPaymentMethodId] = useState("none");
  const [description, setDescription] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);

  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [accDialogOpen, setAccDialogOpen] = useState(false);
  const [manualAccName, setManualAccName] = useState("");

  const [fType, setFType] = useState("all");
  const [fAccount, setFAccount] = useState("all");
  const [fTag, setFTag] = useState("all");
  const [fPaymentMethod, setFPaymentMethod] = useState("all");
  const [fStartDate, setFStartDate] = useState("");
  const [fEndDate, setFEndDate] = useState("");
  const [fSearch, setFSearch] = useState("");

  const [invDialogOpen, setInvDialogOpen] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);

  const [historyStatus, setHistoryStatus] = useState({ can_undo: false, can_redo: false });

  const fetchHistoryStatus = useCallback(async () => {
    if (isStaff) {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/transactions/history/status`, { withCredentials: true });
        setHistoryStatus(res.data);
      } catch (e) {}
    }
  }, [BACKEND_URL, isStaff]);

  const fetchData = useCallback(async () => {
    try {
      const [txRes, accRes, tagRes, pmRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/transactions`, {
          params: {
            type: fType !== "all" ? fType : undefined,
            account_id: fAccount !== "all" ? fAccount : undefined,
            tag_id: fTag !== "all" ? fTag : undefined,
            payment_method_id: fPaymentMethod !== "all" ? fPaymentMethod : undefined,
            start_date: fStartDate || undefined,
            end_date: fEndDate || undefined,
            search: fSearch || undefined
          },
          withCredentials: true
        }),
        isStaff ? axios.get(`${BACKEND_URL}/api/accounts`, { withCredentials: true }) : Promise.resolve({ data: [] }),
        axios.get(`${BACKEND_URL}/api/tags`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/payment_methods`, { withCredentials: true })
      ]);
      setTransactions(txRes.data);
      if (isStaff) setAccounts(accRes.data);
      setTags(tagRes.data);
      setPaymentMethods(pmRes.data);
      fetchHistoryStatus();
    } catch (e) {
      toast.error("Veriler alınamadı.");
    }
  }, [fType, fAccount, fTag, fPaymentMethod, fStartDate, fEndDate, fSearch, BACKEND_URL, isStaff, fetchHistoryStatus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!amount) return;
    if (accountId === "manual" && !accountName.trim()) {
      toast.error("Lütfen bir cari hesap seçin veya elle bir isim girin.");
      return;
    }

    let finalAccId = accountId === "manual" ? null : accountId;
    let finalAccName = accountId === "manual" ? accountName : accounts.find(a => a.id === accountId)?.name;
    let finalPmId = paymentMethodId === "none" ? null : paymentMethodId;
    let finalPmName = paymentMethodId === "none" ? null : paymentMethods.find(p => p.id === paymentMethodId)?.name;

    try {
      if (editTxId) {
        await axios.put(`${BACKEND_URL}/api/transactions/${editTxId}`, {
          type,
          date: new Date(date).toISOString(),
          amount: parseFloat(amount),
          currency,
          account_id: finalAccId,
          account_name: finalAccName,
          payment_method_id: finalPmId,
          payment_method_name: finalPmName,
          description,
          tags: selectedTags
        }, { withCredentials: true });
        toast.success("İşlem güncellendi.");
      } else {
        await axios.post(`${BACKEND_URL}/api/transactions`, {
          type,
          date: new Date(date).toISOString(),
          amount: parseFloat(amount),
          currency,
          account_id: finalAccId,
          account_name: finalAccName,
          payment_method_id: finalPmId,
          payment_method_name: finalPmName,
          description,
          tags: selectedTags
        }, { withCredentials: true });
        toast.success("İşlem eklendi.");
      }
      
      setAmount("");
      setDescription("");
      setAccountName("");
      setAccountId("manual");
      setManualAccName("");
      setPaymentMethodId("none");
      setSelectedTags([]);
      setTxDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem eklenemedi/güncellenemedi.");
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", type);
    console.log("Uploading file with type:", type);

    try {
      const res = await axios.post(`${BACKEND_URL}/api/transactions/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true
      });
      toast.success(`${res.data.inserted} işlem başarıyla eklendi.`);
      setTxDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Excel yüklenirken bir hata oluştu.");
    }
    // reset input
    e.target.value = null;
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/transactions/${id}`, { withCredentials: true });
      toast.success("İşlem silindi.");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silinemedi.");
    }
  };

  const handleUndo = async () => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/transactions/undo`, {}, { withCredentials: true });
      if (res.data.status === "ok") {
        toast.success("Son işlem başarıyla geri alındı.");
        fetchData();
      } else {
        toast.error(res.data.message || "İşlem geri alınamadı.");
      }
    } catch (e) {
      toast.error("Geri alma işlemi başarısız.");
    }
  };

  const handleRedo = async () => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/transactions/redo`, {}, { withCredentials: true });
      if (res.data.status === "ok") {
        toast.success("Geri alınan işlem başarıyla ileri alındı (tekrar uygulandı).");
        fetchData();
      } else {
        toast.error(res.data.message || "İşlem ileri alınamadı.");
      }
    } catch (e) {
      toast.error("İleri alma işlemi başarısız.");
    }
  };

  const handleExport = () => {
    let url = `${BACKEND_URL}/api/export?`;
    if (fType !== "all") url += `type=${fType}&`;
    if (fAccount !== "all") url += `account_id=${fAccount}&`;
    if (fTag !== "all") url += `tag_id=${fTag}&`;
    if (fPaymentMethod !== "all") url += `payment_method_id=${fPaymentMethod}&`;
    if (fStartDate) url += `start_date=${fStartDate}&`;
    if (fEndDate) url += `end_date=${fEndDate}&`;
    if (fSearch) url += `search=${fSearch}&`;
    window.open(url, '_blank');
  };

  const toggleTag = (tagId) => {
    setSelectedTags(prev => prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]);
  };

  const handleAddNewTag = async () => {
    if (!newTag.trim()) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/tags`, { name: newTag.trim(), color: "#0A0A0A" }, { withCredentials: true });
      toast.success("Yeni etiket oluşturuldu.");
      setTags([...tags, res.data]);
      setSelectedTags([...selectedTags, res.data.id]);
      setNewTag("");
    } catch (e) {
      toast.error("Etiket oluşturulamadı.");
    }
  };

  const openTxModal = (selectedType) => {
    setEditTxId(null);
    setAmount("");
    setDescription("");
    setAccountName("");
    setAccountId("manual");
    setManualAccName("");
    setPaymentMethodId("none");
    setSelectedTags([]);
    setDate(new Date().toISOString().split('T')[0]);
    setType(selectedType);
    setTxDialogOpen(true);
  };

  const handleEditClick = (tx) => {
    setEditTxId(tx.id);
    setType(tx.type);
    setDate(new Date(tx.date).toISOString().split('T')[0]);
    setAmount(tx.amount.toString());
    setCurrency(tx.currency || "TL");
    if (tx.account_id) {
      setAccountId(tx.account_id);
      setAccountName(tx.account_name || "");
      setManualAccName("");
    } else if (tx.account_name) {
      setAccountId("manual");
      setAccountName(tx.account_name);
      setManualAccName(tx.account_name);
    } else {
      setAccountId("manual");
      setAccountName("");
      setManualAccName("");
    }
    setPaymentMethodId(tx.payment_method_id || "none");
    setDescription(tx.description || "");
    setSelectedTags(tx.tags || []);
    setTxDialogOpen(true);
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-border pb-4 gap-4">
        <h1 className="text-3xl font-heading font-bold">{isStaff ? "İşlemler" : "Hesap Ekstresi"}</h1>
        {isStaff && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              disabled={!historyStatus.can_undo}
              onClick={handleUndo}
              title="Geri Al"
              className="h-10 w-10 shrink-0 text-muted-foreground hover:text-foreground"
              data-testid="undo-btn"
            >
              <Undo2 size={16} />
            </Button>
            <Button
              variant="outline"
              size="icon"
              disabled={!historyStatus.can_redo}
              onClick={handleRedo}
              title="İleri Al"
              className="h-10 w-10 shrink-0 text-muted-foreground hover:text-foreground mr-1"
              data-testid="redo-btn"
            >
              <Redo2 size={16} />
            </Button>
            <div className="w-px h-6 bg-border mx-1"></div>
            <Button 
              onClick={() => openTxModal('income')} 
              className="bg-income hover:bg-income/90 text-white rounded-sm h-10 px-4 md:px-5 text-[11px] font-bold uppercase tracking-wider shadow-sm"
              data-testid="add-income-btn"
            >
              <Plus size={15} className="mr-1.5" /> Yeni Gelir Ekle
            </Button>
            <Button 
              onClick={() => openTxModal('expense')} 
              className="bg-expense hover:bg-expense/90 text-white rounded-sm h-10 px-4 md:px-5 text-[11px] font-bold uppercase tracking-wider shadow-sm"
              data-testid="add-expense-btn"
            >
              <Plus size={15} className="mr-1.5" /> Yeni Gider Ekle
            </Button>
            <Button 
              onClick={() => openTxModal('debt_payment')} 
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm h-10 px-4 md:px-5 text-[11px] font-bold uppercase tracking-wider shadow-sm"
              data-testid="add-debt-btn"
            >
              <Plus size={15} className="mr-1.5" /> Yeni Borç Ödeme
            </Button>
          </div>
        )}
      </div>

      <Dialog open={txDialogOpen} onOpenChange={setTxDialogOpen}>
        <DialogContent className="sm:max-w-[750px] p-0 overflow-hidden gap-0 rounded-sm">
          <DialogHeader className="p-6 border-b border-border bg-white">
            <DialogTitle className="font-heading tracking-tight text-xl">{editTxId ? "İşlemi Düzenle" : "Yeni İşlem Ekle"}</DialogTitle>
          </DialogHeader>
          <div className="p-6 md:p-8 max-h-[75vh] overflow-y-auto bg-background">
            <TransactionForm 
              handleCreate={handleCreate} type={type} setType={setType} date={date} setDate={setDate}
              amount={amount} setAmount={setAmount} currency={currency} setCurrency={setCurrency}
              accountId={accountId} setAccountId={setAccountId} accountName={accountName} setAccountName={setAccountName}
              paymentMethodId={paymentMethodId} setPaymentMethodId={setPaymentMethodId} paymentMethods={paymentMethods}
              description={description} setDescription={setDescription} accounts={accounts} 
              accDialogOpen={accDialogOpen} setAccDialogOpen={setAccDialogOpen} manualAccName={manualAccName}
              setManualAccName={setManualAccName} selectedTags={selectedTags} tags={tags} toggleTag={toggleTag}
              tagDialogOpen={tagDialogOpen} setTagDialogOpen={setTagDialogOpen} newTag={newTag} 
              setNewTag={setNewTag} handleAddNewTag={handleAddNewTag} handleUpload={handleUpload}
            />
          </div>
        </DialogContent>
      </Dialog>

      <div className="space-y-4 pt-2">
        <TransactionFilters 
          fType={fType} setFType={setFType}
          fAccount={fAccount} setFAccount={setFAccount} accounts={accounts}
          fTag={fTag} setFTag={setFTag} tags={tags}
          fPaymentMethod={fPaymentMethod} setFPaymentMethod={setFPaymentMethod} paymentMethods={paymentMethods}
          fStartDate={fStartDate} setFStartDate={setFStartDate}
          fEndDate={fEndDate} setFEndDate={setFEndDate}
          fSearch={fSearch} setFSearch={setFSearch}
          handleExport={handleExport}
          isAdmin={isStaff}
        />
        <TransactionTable 
          transactions={transactions} 
          tags={tags} 
          handleDelete={handleDelete} 
          handleEditClick={handleEditClick}
          handleMatchInvoice={(tx) => { setSelectedTx(tx); setInvDialogOpen(true); }}
          isAdmin={isStaff}
        />
      </div>

      <TransactionInvoiceDialog 
        transaction={selectedTx} 
        open={invDialogOpen} 
        onOpenChange={setInvDialogOpen} 
        onUpdate={fetchData} 
      />
    </div>
  );
}
